import { createAsyncThunk } from "@reduxjs/toolkit";
import { mapLookup, displayErrors } from "common/helpers";
import { BaseApiResponse } from "common/types";
import { wrapRequest } from "common/utils";
import { lookupSvc, scenarioGroupService as service } from "services/serviceStorage";
import {
    RunExternalFunctionsRequest,
    SaveScenarioGroupLinksRequest,
    ExternalFunction,
    ScenarioGroupFileChangeRequest,
    ScenarioGroupFileItem,
    ScenarioGroupFileRow,
    ScenarioGroupFileSpecificationEntry,
    ScenarioGroupFilesContentRequest,
    ScenarioGroupGridItem,
    ScenarioGroupLinksFilter,
    ScenarioGroupLinksResponse,
    ScenarioGroupLinkedScenario,
    ScenarioGroupListResponse,
    ScenarioGroupLookups,
    ScenarioGroupsGridFilter,
    UploadScenarioGroupInputsParams
} from "./types";
import { scenarioGroupRowStatuses } from "common/enums";

const parseSomDates = (somParams: string | null | undefined) => {
    if (!somParams) {
        return {
            startTime: null,
            endTime: null,
        };
    }

    const parsed = JSON.parse(somParams) as { StartTime?: string; EndTime?: string };
    return {
        startTime: parsed.StartTime ? new Date(parsed.StartTime) : null,
        endTime: parsed.EndTime ? new Date(parsed.EndTime) : null,
    };
};

const mapScenarioGroup = (group: ScenarioGroupGridItem): ScenarioGroupGridItem => {
    const { startTime, endTime } = parseSomDates(group.somParams);

    return {
        ...group,
        key: group.groupId,
        startTime,
        endTime,
    };
};

const mapFiles = (files: ScenarioGroupFileItem[]): ScenarioGroupFileItem[] =>
    files.map((file) => ({
        ...file,
        key: file.fileName.substring(0, file.fileName.indexOf(".")),
    }));

export const fetchGroups = createAsyncThunk<{ data: ScenarioGroupGridItem[]; total: number }, ScenarioGroupsGridFilter>(
    "scenarioGroups/fetchGroups",
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<ScenarioGroupListResponse>>({
            handler: () => service.getGroups(filter as unknown as Record<string, unknown>),
            showNotifications: true
        });

        return {
            data: data?.result.items.map((group) => mapScenarioGroup(group as ScenarioGroupGridItem)) || [],
            total: data?.result.totalCount || 0,
        };
    }
);

export const fetchEditLookups = createAsyncThunk<ScenarioGroupLookups, void>(
    "scenarioGroups/fetchEditLookups",
    async (_, { rejectWithValue }) => {
        try {
            const [somModes, somReleases] = await Promise.all([
                lookupSvc.getSOMModes(),
                lookupSvc.getSOMReleases(),
            ]);

            return {
                somModes: somModes.data.result.map(mapLookup),
                somReleases: somReleases.data.result.map(mapLookup),
            };
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error);
        }
    }
);

export const saveGroup = createAsyncThunk<number, Record<string, unknown>>(
    "scenarioGroups/saveGroup",
    async (group) => {
        const { data } = await wrapRequest<BaseApiResponse<number>>({
            handler: () => service.saveGroup(group),
            showNotifications: true
        });

        return data!.result;
    }
);

export const fetchGroup = createAsyncThunk<ScenarioGroupGridItem, number>(
    "scenarioGroups/fetchGroup",
    async (groupId) => {
        const { data } = await wrapRequest<BaseApiResponse<ScenarioGroupGridItem>>({
            handler: () => service.getGroup(groupId),
            showNotifications: true
        });

        return mapScenarioGroup(data!.result);
    }
);

export const deleteGroups = createAsyncThunk<void, { groupIds: number[]; deleteScenarios: boolean }>(
    "scenarioGroups/deleteGroups",
    async ({ groupIds, deleteScenarios }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteGroups(groupIds, deleteScenarios),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const runSOM = createAsyncThunk<void, number[]>(
    "scenarioGroups/runSOM",
    async (groupIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runSOM(groupIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const stopSOM = createAsyncThunk<void, number[]>(
    "scenarioGroups/stopSOM",
    async (groupIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.stopSOM(groupIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const runPSO = createAsyncThunk<void, number[]>(
    "scenarioGroups/runPSO",
    async (groupIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runPSO(groupIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const generateSOMInputs = createAsyncThunk<void, number[]>(
    "scenarioGroups/generateSOMInputs",
    async (groupIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.generateSOMInputs(groupIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const cancelSOMInputs = createAsyncThunk<void, number[]>(
    "scenarioGroups/cancelSOMInputs",
    async (groupIds, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.cancelSOMInputs(groupIds),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const uploadInputs = createAsyncThunk<void, UploadScenarioGroupInputsParams>(
    "scenarioGroups/uploadInputs",
    async ({ groupId, files }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.uploadInputs(groupId, files),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const fetchInputs = createAsyncThunk<ScenarioGroupFileItem[], number>(
    "scenarioGroups/fetchInputs",
    async (groupId) => {
        const { data } = await wrapRequest<BaseApiResponse<ScenarioGroupFileItem[]>>({
            handler: () => service.getInputFiles(groupId),
            showNotifications: true
        });

        return mapFiles(data?.result || []);
    }
);

export const inputsExist = createAsyncThunk<boolean[], { groupIds: number[]; fileNames?: string[] }>(
    "scenarioGroups/inputsExist",
    async ({ groupIds, fileNames = [] }) => {
        const responses = await Promise.all(groupIds.map((groupId) =>
            wrapRequest<BaseApiResponse<boolean>>({
                handler: () => service.inputsExist(groupId, fileNames),
                showNotifications: true
            })
        ));

        return responses.map((response) => response.data?.result || false);
    }
);

export const fetchResults = createAsyncThunk<ScenarioGroupFileItem[], number>(
    "scenarioGroups/fetchResults",
    async (groupId) => {
        const { data } = await wrapRequest<BaseApiResponse<ScenarioGroupFileItem[]>>({
            handler: () => service.getResultFiles(groupId),
            showNotifications: true
        });

        return mapFiles(data?.result || []);
    }
);

export const deleteInputs = createAsyncThunk<void, { groupId: number; fileNames: string[] }>(
    "scenarioGroups/deleteInputs",
    async ({ groupId, fileNames }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteInputFiles(groupId, fileNames),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const copyInputs = createAsyncThunk<void, { sourceId: number; destinationId: number }>(
    "scenarioGroups/copyInputs",
    async ({ sourceId, destinationId }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.copyInputFiles(sourceId, destinationId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const fetchLinks = createAsyncThunk<{ data: ScenarioGroupLinkedScenario[]; total: number }, Partial<ScenarioGroupLinksFilter>>(
    "scenarioGroups/fetchLinks",
    async (filter) => {
        const { data } = await wrapRequest<BaseApiResponse<ScenarioGroupLinksResponse>>({
            handler: () => service.getLinks(filter),
            showNotifications: true
        });

        return {
            data: data?.result.items.map((scenario) => ({
                ...scenario,
                key: scenario.scenarioId,
            })) || [],
            total: data?.result.totalCount || 0,
        };
    }
);

export const saveLinks = createAsyncThunk<void, SaveScenarioGroupLinksRequest>(
    "scenarioGroups/saveLinks",
    async ({ groupId, scenarioIds, action }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.saveLinks({ groupId, scenarioIds, action }),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const fetchResultUrls = createAsyncThunk<string[], { groupId: number; fileNames: string[] }>(
    "scenarioGroups/fetchResultUrls",
    async ({ groupId, fileNames }) => {
        const { data } = await wrapRequest<BaseApiResponse<string[]>>({
            handler: () => service.getResultUrls(groupId, fileNames),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchExternalFunctions = createAsyncThunk<ExternalFunction[], number>(
    "scenarioGroups/fetchExternalFunctions",
    async (taskId) => {
        const { data } = await wrapRequest<BaseApiResponse<ExternalFunction[]>>({
            handler: () => service.getExternalFunctions(taskId),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const runExternalFunctions = createAsyncThunk<void, RunExternalFunctionsRequest>(
    "scenarioGroups/runExternalFunctions",
    async (request, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.runExternalFunctions(request),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const getFilesContent = createAsyncThunk<Record<string, ScenarioGroupFileRow[]>, ScenarioGroupFilesContentRequest>(
    "scenarioGroups/getFilesContent",
    async (request) => {
        const { data } = await wrapRequest<Record<string, string>>({
            handler: () => service.getFilesContent(request),
            showNotifications: true
        });

        const source: Record<string, ScenarioGroupFileRow[]> = {};

        for (const [name, content] of Object.entries(data || {})) {
            const entries = JSON.parse(content) || [];
            source[name] = entries.length > 0
                ? entries.map((row: unknown, index: number) => ({
                    index,
                    type: scenarioGroupRowStatuses.unchanged,
                    data: row as Record<string, unknown>,
                    errors: {},
                }))
                : entries;
        }

        return source;
    }
);

export const commitFilesChanges = createAsyncThunk<unknown[], ScenarioGroupFileChangeRequest[]>(
    "scenarioGroups/commitFilesChanges",
    async (requests) => {
        const responses = await Promise.all(requests.map((request) =>
            wrapRequest<unknown>({
                handler: () => service.commitFilesChanges(request),
                showNotifications: true
            })
        ));

        return responses.map((response) => response.data);
    }
);

export const getFilesSpecification = createAsyncThunk<ScenarioGroupFileSpecificationEntry[], number>(
    "scenarioGroups/getFilesSpecification",
    async (groupId) => {
        const { data } = await wrapRequest<BaseApiResponse<ScenarioGroupFileSpecificationEntry[]>>({
            handler: () => service.getFilesSpecification(groupId),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchInputUrls = createAsyncThunk<string[], { groupId: number; fileNames: string[] }>(
    "scenarioGroups/fetchInputUrls",
    async ({ groupId, fileNames }) => {
        const { data } = await wrapRequest<BaseApiResponse<string[]>>({
            handler: () => service.getInputUrls(groupId, fileNames),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchLogUrl = createAsyncThunk<string, number>(
    "scenarioGroups/fetchLogUrl",
    async (groupId) => {
        const { data } = await wrapRequest<BaseApiResponse<string>>({
            handler: () => service.fetchLogUrl(groupId),
            showNotifications: true
        });

        return data?.result || "";
    }
);
